import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { 
  Users, Building2, Calendar, Ticket, DollarSign, 
  TrendingUp, Shield, Trash2, Eye 
} from 'lucide-react';
import { toast } from 'sonner';
import { ReportManifestManager } from '@/components/report/ReportManifestManager';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = ['tenants', 'users', 'events', 'report'].includes(searchParams.get('tab')) ? searchParams.get('tab') : 'tenants';
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role !== 'super_admin') {
      toast.error('Accesso negato');
      navigate('/dashboard');
      return;
    }
    fetchAllData();
  }, [user]);

  const fetchAllData = async () => {
    try {
      const [statsRes, tenantsRes, usersRes, eventsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/stats`),
        axios.get(`${API_URL}/admin/tenants`),
        axios.get(`${API_URL}/admin/users`),
        axios.get(`${API_URL}/admin/events`)
      ]);
      
      setStats(statsRes.data);
      setTenants(tenantsRes.data);
      setUsers(usersRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const deleteTenant = async (tenantId) => {
    if (!window.confirm('Sei sicuro? Questo eliminerà TUTTO (utenti, eventi, biglietti)')) return;
    try {
      await axios.delete(`${API_URL}/admin/tenants/${tenantId}`);
      toast.success('Tenant eliminato');
      fetchAllData();
    } catch (error) {
      toast.error('Errore durante eliminazione');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-gray-600">Caricamento...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-panel">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 data-testid="admin-panel-heading" className="text-3xl font-bold text-gray-900">Admin Panel</h1>
            <p className="text-gray-600 mt-1">Gestione piattaforma ZonePass</p>
          </div>
          <div data-testid="admin-role-badge" className="flex items-center gap-2">
            <Shield className="w-8 h-8 text-red-600" />
            <span className="text-lg font-semibold text-red-600">SUPER ADMIN</span>
          </div>
        </div>

        {/* Platform Stats */}
        {stats && activeTab !== 'report' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card className="border-l-4 border-l-blue-600">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Tenant Totali</CardTitle>
                <Building2 className="w-4 h-4 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-gray-900">{stats.total_tenants}</div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-green-600">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Utenti Totali</CardTitle>
                <Users className="w-4 h-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-gray-900">{stats.total_users}</div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-purple-600">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Eventi Totali</CardTitle>
                <Calendar className="w-4 h-4 text-purple-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-gray-900">{stats.total_events}</div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-orange-600">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Biglietti Venduti</CardTitle>
                <Ticket className="w-4 h-4 text-orange-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-gray-900">{stats.total_tickets}</div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-red-600">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Ordini Totali</CardTitle>
                <TrendingUp className="w-4 h-4 text-red-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-gray-900">{stats.total_orders}</div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-yellow-600">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Revenue Piattaforma</CardTitle>
                <DollarSign className="w-4 h-4 text-yellow-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-gray-900">€{stats.total_revenue.toFixed(2)}</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Data Tables */}
        <Tabs value={activeTab} onValueChange={(tab) => setSearchParams({ tab })} className="w-full min-w-0">
          <TabsList data-testid="admin-tabs" className="grid h-auto w-full grid-cols-2 gap-1 sm:grid-cols-4">
            <TabsTrigger data-testid="admin-tab-tenants" value="tenants">Tenants ({tenants.length})</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-users" value="users">Utenti ({users.length})</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-events" value="events">Eventi ({events.length})</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-report" value="report">Report &amp; Note</TabsTrigger>
          </TabsList>

          <TabsContent data-testid="admin-report-content" value="report" className="mt-8 min-w-0">
            <ReportManifestManager />
          </TabsContent>

          <TabsContent value="tenants" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Tutti i Tenant</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subdomain</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo Account</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Eventi</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Utenti</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Creato</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Azioni</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {tenants.map(tenant => (
                        <tr key={tenant.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{tenant.name}</div>
                            {tenant.company_name && (
                              <div className="text-xs text-gray-600">{tenant.company_name}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">{tenant.subdomain}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                              tenant.account_type === 'company' 
                                ? 'bg-blue-100 text-blue-800' 
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {tenant.account_type || 'N/A'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center text-gray-900">{tenant.events_count}</td>
                          <td className="px-4 py-3 text-center text-gray-900">{tenant.users_count}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {new Date(tenant.created_at).toLocaleDateString('it-IT')}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => deleteTenant(tenant.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="users" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Tutti gli Utenti</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tenant</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ruolo</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Creato</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {users.map(u => (
                        <tr key={u.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-900">{u.full_name}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{u.email}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{u.tenant_name}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                              u.role === 'super_admin' ? 'bg-red-100 text-red-800' :
                              u.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {u.role}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {new Date(u.created_at).toLocaleDateString('it-IT')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="events" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Tutti gli Eventi</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Titolo</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tenant</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {events.map(event => (
                        <tr key={event.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-900">{event.title}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{event.tenant_name}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{event.location}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {new Date(event.start_date).toLocaleDateString('it-IT')}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                              event.status === 'published' ? 'bg-green-100 text-green-800' :
                              event.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {event.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
